import React, { useState } from "react";
import { Avatar, Divider, Typography, Button, message } from "antd"; //  message import kiya
import {
  FilePdfOutlined,
  FileExcelOutlined,
  FileImageOutlined,
  PlayCircleOutlined,
  FileOutlined,
} from "@ant-design/icons";
import DownloadSquareIcon from "../../../../Theme/CustomIcons/Icons/Arrow/DownloadSquareIcon";
import { useSelector } from "react-redux";

const { Text } = Typography;

const isOnlyEmojis = (text) => {
  if (!text) return false;
  const clean = text.replace(/<[^>]*>/g, "").trim();
  if (!clean) return false;
  const emojiRegex = /[\u{1F300}-\u{1F9FF}\u{1F600}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{1F1E6}-\u{1F1FF}\u{1F191}-\u{1F251}\u{1F900}-\u{1F9FF}\u{1FA70}-\u{1FAFF}\u{1F300}-\u{1F5FF}\u{1F900}-\u{1F9FF}]/gu;
  const remaining = clean.replace(emojiRegex, "").replace(/\s/g, "");
  return remaining.length === 0;
};

const parseMarkdown = (text) => {
  if (!text) return "";

  // If it's already HTML (e.g. from rich text editor), return as is
  const isHtml = /<[a-z][\s\S]*>/i.test(text);
  if (isHtml) return text;

  // Escape HTML first to prevent XSS
  let escaped = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Bold: **text**
  escaped = escaped.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Italic: *text*
  escaped = escaped.replace(/\*(.*?)\*/g, "<em>$1</em>");

  // Code: `code`
  escaped = escaped.replace(/`(.*?)`/g, '<code style="background: rgba(255,255,255,0.12); padding: 2px 6px; border-radius: 4px; font-family: monospace; font-size: 0.9em;">$1</code>');

  // Split lines to handle lists and line breaks
  const lines = escaped.split("\n");
  let inList = false;
  let listType = null; // 'ul' or 'ol'
  const resultLines = [];

  lines.forEach((line) => {
    const trimmed = line.trim();
    const bulletMatch = line.match(/^(\s*)([•\*\-])\s+(.*)$/);
    const numberMatch = line.match(/^(\s*)(\d+)\.\s+(.*)$/);

    if (bulletMatch) {
      if (!inList || listType !== "ul") {
        if (inList) resultLines.push(`</${listType}>`);
        resultLines.push('<ul style="margin: 4px 0; padding-left: 20px; list-style-type: disc;">');
        inList = true;
        listType = "ul";
      }
      resultLines.push(`<li>${bulletMatch[3]}</li>`);
    } else if (numberMatch) {
      if (!inList || listType !== "ol") {
        if (inList) resultLines.push(`</${listType}>`);
        resultLines.push('<ol style="margin: 4px 0; padding-left: 20px; list-style-type: decimal;">');
        inList = true;
        listType = "ol";
      }
      resultLines.push(`<li>${numberMatch[3]}</li>`);
    } else {
      if (inList) {
        resultLines.push(`</${listType}>`);
        inList = false;
        listType = null;
      }
      resultLines.push(line);
    }
  });

  if (inList) {
    resultLines.push(`</${listType}>`);
  }

  return resultLines.join("<br />");
};

//  1. FILENAME EXTRACT FROM publicId (Split from 'uploads/')
const getFileName = (fileObj) => {
  if (!fileObj) return "Attachment";
  const path = fileObj.publicId || fileObj.url || "";
  const parts = path.split("uploads/"); // 'uploads/' ke baad wala part lo

  if (parts.length > 1 && parts[1].trim() !== "") {
    // Aapne kaha tha small karna hai array mein, toh lowercase kar rahe
    return parts[1].trim().toLowerCase();
  }
  return "Attachment"; // Agar kuch nhi aye toh yeh
};

//  2. FILE TYPE CHECK (Extension se)
const getFileType = (fileName) => {
  if (!fileName || fileName === "Attachment") return "file";
  const ext = fileName.split(".").pop().toLowerCase();
  if (["pdf"].includes(ext)) return "pdf";
  if (["csv", "xlsx", "xls"].includes(ext)) return "excel";
  if (["png", "jpg", "jpeg", "gif", "webp"].includes(ext)) return "image";
  if (["mp4", "mov", "avi"].includes(ext)) return "video";
  return "file";
};

//  3. ICONS
const getFileIcon = (type) => {
  if (type === "pdf") return <FilePdfOutlined style={{ color: "#ff4d4f" }} />;
  if (type === "excel")
    return <FileExcelOutlined style={{ color: "#52c41a" }} />;
  if (type === "image")
    return <FileImageOutlined style={{ color: "#1890ff" }} />;
  if (type === "video")
    return <PlayCircleOutlined style={{ color: "#722ed1" }} />;
  return <FileOutlined style={{ color: "#8c8c8c" }} />;
};

//  4. DIRECT BLOB DOWNLOAD (New tab nahi khulega, same tab direct download)
const handleDirectDownload = async (fileUrl, fileName) => {
  if (!fileUrl) {
    message.error("Download link not available");
    return;
  }
  try {
    message.loading({
      content: "Downloading...",
      key: `dl-${fileName}`,
      duration: 0,
    });
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = fileName; // Yehi name file ka hoga
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    message.success({
      content: "Downloaded!",
      key: `dl-${fileName}`,
      duration: 2,
    });
  } catch (error) {
    message.error({
      content: "Download failed!",
      key: `dl-${fileName}`,
      duration: 2,
    });
  }
};

const ChatMessage = ({ message, isCurrentUser }) => {
  const themeDefault = useSelector((state) => state.themeDefault);
  const primaryColor = themeDefault?.info?.colorPrimary || "#1890ff";
  const radius = themeDefault?.info?.borderRadius || 8;

  const [expanded, setExpanded] = useState(false);
  const maxPreviewLength = 250;

  const bubbleStyle = {
    maxWidth: "65%",
    minWidth: "90px",
    padding: "8px 16px 20px 16px",
    fontSize: 14.5,
    lineHeight: 1.45,
    wordBreak: "break-word",
    position: "relative",
    boxShadow: "0 1.5px 2px rgba(0,0,0,0.12)",
    display: "inline-block",
  };

  const currentUserBubble = {
    background: primaryColor,
    color: "#ffffff",
    borderRadius: "16px 4px 16px 16px",
  };

  const otherUserBubble = {
    background: "#ffffff",
    color: "#111b21",
    borderRadius: "4px 16px 16px 16px",
  };

  const aiUserBubble = {
    background: "linear-gradient(135deg, #f5f3ff 0%, #edd8ff 100%)",
    color: "#111b21",
    border: "1px solid rgba(130, 80, 250, 0.25)",
    borderRadius: "4px 16px 16px 16px",
    boxShadow: "0 1.5px 3px rgba(130, 80, 250, 0.15)",
  };

  const renderMessageText = () => {
    if (!message.text) return null;
    if (message.text.length <= maxPreviewLength || expanded)
      return message.text;
    return `${message.text.slice(0, maxPreviewLength)}...`;
  };

  const emojiOnly = isOnlyEmojis(message.text);

  if (emojiOnly) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: isCurrentUser ? "flex-end" : "flex-start",
          marginBottom: 10,
        }}>
        <div
          dangerouslySetInnerHTML={{ __html: parseMarkdown(message.text) }}
          style={{ fontSize: 44, lineHeight: 1.2, userSelect: "text" }}
        />
      </div>
    );
  }

  // Format compact timestamp like WhatsApp (e.g. "02:47 PM")
  const getWhatsAppTime = (timeStr) => {
    if (!timeStr) return "";
    const parts = timeStr.split(" · ");
    return parts.length > 1 ? parts[1] : timeStr;
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: isCurrentUser ? "row-reverse" : "row",
        alignItems: "flex-start",
        gap: 8,
        marginBottom: 8,
        width: "100%",
        padding: isCurrentUser ? "0 18px 0 30px" : "0 30px 0 18px",
      }}>
      {/* AVATAR (Only for other users / AI) */}
      {!isCurrentUser && (
        <Avatar
          size={24}
          src={message.avatar || undefined}
          style={{
            background: message.isAI ? "rgba(130, 80, 250, 0.2)" : "#d9d9d9",
            color: message.isAI ? "var(--primary-color, #722ed1)" : "#666",
            fontWeight: 600,
            flexShrink: 0,
            fontSize: 10,
          }}
          onError={() => true}>
          {message.initials ||
            (message.senderName
              ?.split(" ")
              .map((n) => n[0])
              .join("")
              .toUpperCase()
              .slice(0, 2) || "U")}
        </Avatar>
      )}

      {/* BUBBLE */}
      <div
        style={{
          ...bubbleStyle,
          ...(isCurrentUser ? currentUserBubble : message.isAI ? aiUserBubble : otherUserBubble),
        }}>
        {/* SENDER NAME (only for other users) */}
        {!isCurrentUser && (
          <div style={{ display: "block", fontSize: 12, color: message.isAI ? "#8250fa" : "#53bdeb", marginBottom: 3, fontWeight: 600 }}>
            {message.senderName}
          </div>
        )}

        <div
          className="message-content-html"
          dangerouslySetInnerHTML={{
            __html: parseMarkdown(renderMessageText()),
          }}
          style={{ display: "inline" }}
        />
        {message.text &&
          message.text.length > maxPreviewLength &&
          !expanded && (
            <Button
              type="link"
              size="small"
              onClick={() => setExpanded(true)}
              style={{ padding: 0, marginLeft: 5, color: "#53bdeb", textDecoration: "underline" }}>
              Read more
            </Button>
          )}

        {/* FILES SECTION */}
        {message.files && message.files.length > 0 && (
          <div
            style={{
              marginTop: 6,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}>
            {message.files.map((file, index) => {
              const fileName = getFileName(file);
              const fileType = getFileType(fileName);
              const fileUrl = file.url;

              if (fileType === "image" || fileType === "video") {
                return (
                  <div
                    key={index}
                    style={{
                      position: "relative",
                      borderRadius: 6,
                      overflow: "hidden",
                      maxWidth: 240,
                      border: "1px solid rgba(0,0,0,0.08)",
                    }}>
                    {fileType === "image" ? (
                      <img
                        src={fileUrl}
                        alt={fileName}
                        style={{
                          width: "100%",
                          maxHeight: 150,
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    ) : (
                      <video
                        src={fileUrl}
                        controls
                        style={{
                          width: "100%",
                          maxHeight: 150,
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    )}
                    <Button
                      icon={<DownloadSquareIcon size="small" style={{ color: "#fff" }} />}
                      onClick={() => handleDirectDownload(fileUrl, fileName)}
                      style={{
                        position: "absolute",
                        bottom: 6,
                        right: 6,
                        background: "rgba(0,0,0,0.6)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "50%",
                        width: 28,
                        height: 28,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backdropFilter: "blur(2px)",
                      }}
                    />
                  </div>
                );
              }

              const tagStyle = isCurrentUser
                ? {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 12px",
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  borderRadius: 6,
                  cursor: "pointer",
                  gap: 10,
                  border: "1px solid rgba(255, 255, 255, 0.25)",
                  color: "#ffffff",
                }
                : {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 12px",
                  backgroundColor: themeDefault?.info?.newConfig?.colorPrimaryShades?.[0] || "rgba(24, 144, 255, 0.08)",
                  borderRadius: 6,
                  cursor: "pointer",
                  gap: 10,
                  border: `1px solid ${themeDefault?.info?.newConfig?.colorPrimaryShades?.[2] || "rgba(24, 144, 255, 0.25)"}`,
                  color: primaryColor,
                };

              return (
                <div
                  key={index}
                  onClick={() => handleDirectDownload(fileUrl, fileName)}
                  style={tagStyle}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    {getFileIcon(fileType)}
                    <Text
                      ellipsis
                      style={{
                        fontSize: 12,
                        maxWidth: 160,
                        color: "inherit",
                      }}>
                      {fileName}
                    </Text>
                  </div>
                  <DownloadSquareIcon
                    size="small"
                    style={{
                      color: "inherit",
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}

        {/* COMPACT WHATSAPP-LIKE TIMESTAMP INLINE */}
        <span
          style={{
            position: "absolute",
            bottom: 3,
            right: 8,
            fontSize: 9.5,
            color: isCurrentUser ? "rgba(255, 255, 255, 0.75)" : "#667781",
            userSelect: "none",
          }}>
          {getWhatsAppTime(message.timestamp)}
        </span>
      </div>
    </div>
  );
};

export default ChatMessage;
